"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { AuthCredentialKind, AuthSuccess } from "@/components/auth/types";
import { GoogleSignUpEntry } from "./sign-up-auth";
import { authService } from "@/services/auth-service";

/** A Google sign-in with no patient account at this clinic, held for sign-up. */
export interface PendingGoogleSignUp {
  entry: GoogleSignUpEntry;
  email: string | null;
}

/**
 * State for finishing sign-up in place after a Google sign-in finds no patient
 * at this clinic. Every surface that offers Google hands `continueSignUp` to its
 * post-auth resolver and renders `GoogleSignUpContinuation` while `pending`.
 */
export function useGoogleSignUpContinuation() {
  const $ = _c(6);
  const [pending, setPending] = useState(null);
  const generation = useRef(0);
  const active = useRef(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      active.current = true;
      return () => {
        active.current = false;
        generation.current = generation.current + 1;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async credential => {
      if (credential.kind !== AuthCredentialKind.GOOGLE || !credential.googleCredential) {
        return false;
      }
      const attempt = generation.current = generation.current + 1;
      const email = credential.user.email ?? null;
      try {
        await authService.logout(true);
      } catch {}
      if (!active.current || generation.current !== attempt) {
        return true;
      }
      setPending({
        entry: {
          kind: "CREDENTIAL",
          credential: credential.googleCredential,
          email
        },
        email
      });
      return true;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const continueSignUp = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      generation.current = generation.current + 1;
      setPending(null);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cancel = t3;
  let t4;
  if ($[4] !== pending) {
    t4 = {
      pending,
      continueSignUp,
      cancel
    };
    $[4] = pending;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}